/**
 * The Production panel: "Publish to production" opens the publish dialog
 * (what will be published, from the arche settings, plus "copy test data"),
 * and the panel then follows the submission (`api.getPluginSubmission`) once
 * the forge surfaces its id.
 */
import { Button } from '@/components/ui/button';
import { Loader2, Rocket } from 'lucide-react';
import type { PluginSubmissionDetailResult } from '@/lib/api';
import type { ForgeSummary } from '../types';

export interface ForgePublishOptions {
  carryTestData: boolean;
}

interface ForgePublishPanelProps {
  forge: ForgeSummary;
  submission: PluginSubmissionDetailResult | null;
  /** Opens the publish dialog. */
  onOpenPublish: () => void;
  isPublishRequested: boolean;
}

function submissionLine(submission: PluginSubmissionDetailResult | null): string | null {
  if (!submission) return null;
  const s = submission.submission;
  if (s.errorMessage) return `${s.status}: ${s.errorMessage}`;
  return `Submission ${s.status.replace(/_/g, ' ')}`;
}

export function ForgePublishPanel({
  forge,
  submission,
  onOpenPublish,
  isPublishRequested,
}: ForgePublishPanelProps) {
  const line = submissionLine(submission);
  return (
    <div className="rounded-lg border p-3 space-y-2" data-testid="forge-publish-panel">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-medium">Production</h3>
        <Button size="sm" onClick={onOpenPublish} disabled={isPublishRequested}>
          {isPublishRequested ? (
            <Loader2 className="h-4 w-4 animate-spin mr-1" />
          ) : (
            <Rocket className="h-4 w-4 mr-1" />
          )}
          Publish to production
        </Button>
      </div>
      <p className="text-xs text-muted-foreground">
        Publishes with the arche settings above, through the normal pipeline (security review,
        build). The arche goes live at <code className="font-mono">/a/{forge.archeId}</code>.
      </p>
      {isPublishRequested && !line && (
        <p className="text-xs text-muted-foreground">Packaging the workspace…</p>
      )}
      {line && <p className="text-xs">{line}</p>}
    </div>
  );
}
